import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

type ViewportPosition = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  openAbove: boolean;
};

type MobileAwareAutocompleteProps<T> = {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  /** Only the filter surface visible at this breakpoint may expose suggestions. */
  surface?: "desktop" | "mobile";
  loading?: boolean;
  items: T[];
  listId: string;
  portalMarker?: string;
  /** Portal into the nearest dialog on every viewport to escape clipped scroll surfaces. */
  portalInDialog?: boolean;
  /** Prefer a specific interactive overlay root (e.g. a nested popover). */
  portalTargetSelector?: string;
  /** Constrain suggestions to the overlay's scrollable body, above fixed footers. */
  portalBoundsSelector?: string;
  /**
   * Page-level search fields use a body portal so a scroll container cannot
   * clip their suggestions. A field inside a fixed mobile drawer can opt out:
   * keeping that list in the drawer's flow preserves spatial continuity with
   * the input on iOS, where visual-viewport coordinates are not reliable for
   * a body-fixed layer while the keyboard is open.
   */
  portalOnMobile?: boolean;
  /**
   * Opt-in inline mode (mobile viewports only, < 1024px): suggestions render
   * in document flow under the field — no portal, no overlay — showing at
   * most this many rows, with a "More results" control that reveals the
   * remaining returned rows. Collapses whenever `items` changes (new query).
   * Desktop rendering is untouched.
   */
  inlineLimit?: number;
  getKey: (item: T, index: number) => string;
  activeIndex?: number;
  onSelect: (item: T) => void;
  renderItem: (item: T) => ReactNode;
};

const MOBILE_QUERY = "(max-width: 1023px)";
const VIEWPORT_MARGIN = 8;
const POPOVER_GAP = 4;
const MINIMUM_LIST_HEIGHT = 112;
const MAXIMUM_LIST_HEIGHT = 288;

function getIsMobileViewport() {
  return typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia(MOBILE_QUERY).matches;
}

/**
 * A location suggestion list that remains usable while a mobile keyboard is
 * open. Desktop keeps the normal absolute dropdown; mobile uses a body
 * portal, the visual viewport, and an above/below placement decision.
 */
export function MobileAwareAutocomplete<T>({
  anchorRef,
  open,
  surface,
  loading = false,
  items,
  listId,
  portalMarker = "mobile-autocomplete-portal",
  portalInDialog = false,
  portalTargetSelector,
  portalBoundsSelector,
  portalOnMobile = true,
  inlineLimit,
  getKey,
  activeIndex = -1,
  onSelect,
  renderItem,
}: MobileAwareAutocompleteProps<T>) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(getIsMobileViewport);
  const [position, setPosition] = useState<ViewportPosition | null>(null);
  const inline = inlineLimit != null && inlineLimit > 0 && isMobile;
  const shouldPortal = !inline && (portalInDialog || (isMobile && portalOnMobile));
  const [expanded, setExpanded] = useState(false);
  const itemsRef = useRef(items);
  if (itemsRef.current !== items) {
    itemsRef.current = items;
    if (expanded) setExpanded(false);
  }
  // Keyboard navigation past the inline limit genuinely reveals the rest.
  if (inline && !expanded && activeIndex >= (inlineLimit ?? 0)) setExpanded(true);
  const isActive = surface === undefined || (surface === "mobile") === isMobile;
  const listOpen = open && isActive;

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const update = () => setIsMobile(mediaQuery.matches);
    update();
    mediaQuery.addEventListener?.("change", update);
    mediaQuery.addListener?.(update);
    return () => {
      mediaQuery.removeEventListener?.("change", update);
      mediaQuery.removeListener?.(update);
    };
  }, []);

  useLayoutEffect(() => {
    if (!listOpen || !shouldPortal) {
      setPosition(null);
      return;
    }

    const anchor = anchorRef.current;
    if (!anchor) return;

    const updatePosition = () => {
      const rect = anchor.getBoundingClientRect();
      const visualViewport = window.visualViewport;
      const viewportTop = visualViewport?.offsetTop ?? 0;
      const viewportLeft = visualViewport?.offsetLeft ?? 0;
      const viewportWidth = Math.min(
        visualViewport?.width ?? window.innerWidth,
        Math.max(0, window.innerWidth - viewportLeft),
      );
      const viewportHeight = Math.min(
        visualViewport?.height ?? window.innerHeight,
        Math.max(0, window.innerHeight - viewportTop),
      );
      const viewportBottom = viewportTop + viewportHeight;
      const portalTarget = (portalTargetSelector
        ? anchor.closest<HTMLElement>(portalTargetSelector)
        : null) ?? ((portalInDialog || (isMobile && portalOnMobile))
        ? anchor.closest<HTMLElement>('[role="dialog"]')
        : null);
      const boundsElement = (portalBoundsSelector
        ? anchor.closest<HTMLElement>(portalBoundsSelector)
        : null) ?? portalTarget;
      const overlayRect = boundsElement?.getBoundingClientRect() ?? null;
      const boundsTop = Math.max(viewportTop, overlayRect?.top ?? viewportTop) + VIEWPORT_MARGIN;
      const boundsBottom = Math.max(
        boundsTop,
        Math.min(viewportBottom, overlayRect?.bottom ?? viewportBottom) - VIEWPORT_MARGIN,
      );
      const boundsLeft = Math.max(viewportLeft, overlayRect?.left ?? viewportLeft) + VIEWPORT_MARGIN;
      const boundsRight = Math.min(viewportLeft + viewportWidth, overlayRect?.right ?? viewportLeft + viewportWidth) - VIEWPORT_MARGIN;
      // Keyboard resizing can leave the input outside its scroll body's
      // visible bounds. Measure space inside the body, not up to that input.
      const availableBelow = Math.max(0, boundsBottom - Math.max(rect.bottom, boundsTop) - POPOVER_GAP);
      const availableAbove = Math.max(0, Math.min(rect.top, boundsBottom) - boundsTop - POPOVER_GAP);
      const openAbove =
        availableBelow < MINIMUM_LIST_HEIGHT && availableAbove > availableBelow;
      const availableSpace = Math.min(
        MAXIMUM_LIST_HEIGHT,
        openAbove ? availableAbove : availableBelow,
      );
      const width = Math.min(
        rect.width,
        Math.max(0, boundsRight - boundsLeft),
      );
      const left = Math.max(
        boundsLeft,
        Math.min(rect.left, boundsRight - width),
      );
      const preferredTop = openAbove
        ? rect.top - availableSpace - POPOVER_GAP
        : rect.bottom + POPOVER_GAP;
      const top = Math.max(boundsTop, Math.min(preferredTop, boundsBottom - availableSpace));

      setPosition({
        top,
        left,
        width,
        maxHeight: availableSpace,
        openAbove,
      });
    };

    updatePosition();
    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(updatePosition)
      : null;
    resizeObserver?.observe(anchor);
    const portalTarget = (portalTargetSelector
      ? anchor.closest<HTMLElement>(portalTargetSelector)
      : null) ?? ((portalInDialog || (isMobile && portalOnMobile))
      ? anchor.closest<HTMLElement>('[role="dialog"]')
      : null);
    const boundsElement = (portalBoundsSelector
      ? anchor.closest<HTMLElement>(portalBoundsSelector)
      : null) ?? portalTarget;
    if (portalTarget && portalTarget !== anchor) resizeObserver?.observe(portalTarget);
    if (boundsElement && boundsElement !== anchor && boundsElement !== portalTarget) {
      resizeObserver?.observe(boundsElement);
    }
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.visualViewport?.addEventListener("resize", updatePosition);
    window.visualViewport?.addEventListener("scroll", updatePosition);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      window.visualViewport?.removeEventListener("resize", updatePosition);
      window.visualViewport?.removeEventListener("scroll", updatePosition);
    };
  }, [anchorRef, items.length, loading, listOpen, isMobile, portalInDialog, portalOnMobile, portalTargetSelector, portalBoundsSelector, shouldPortal]);

  if (!listOpen || (!loading && items.length === 0)) return null;

  if (inline) {
    const limit = inlineLimit ?? items.length;
    const visible = expanded ? items : items.slice(0, limit);
    const hidden = items.length - visible.length;
    return (
      <div data-testid="autocomplete-inline" className="mt-1.5 overflow-hidden rounded-xl border border-border/80 bg-popover text-popover-foreground">
        {loading && items.length === 0 ? (
          <div className="px-3 py-3 text-sm text-muted-foreground" role="status">
            {t("search.a11y.searching", { defaultValue: "Searching…" })}
          </div>
        ) : (
          <>
            <ul id={listId} role="listbox" className="divide-y divide-border/60">
              {visible.map((item, index) => (
                <li key={getKey(item, index)} id={`${listId}-option-${index}`} role="option" aria-selected={activeIndex === index}>
                  <button
                    type="button"
                    data-testid={`autocomplete-inline-option-${index}`}
                    className={`flex min-h-12 w-full touch-manipulation items-start gap-2 px-3 py-2.5 text-left text-base text-foreground transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none ${activeIndex === index ? "bg-muted/60" : ""}`}
                    onPointerDown={(event) => { if (event.pointerType !== "touch") event.preventDefault(); }}
                    onClick={() => onSelect(item)}
                  >
                    {renderItem(item)}
                  </button>
                </li>
              ))}
            </ul>
            {hidden > 0 && (
              <button
                type="button"
                data-testid="autocomplete-more-results"
                aria-controls={listId}
                aria-expanded={false}
                // Not a scroll surface: keep input focus for every pointer
                // type so a touch tap cannot blur-close the list it expands.
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => setExpanded(true)}
                className="flex min-h-11 w-full items-center justify-center border-t border-border/60 px-3 text-sm font-medium text-primary hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
              >
                {t("search.more_results", { defaultValue: "More results ({{count}})", count: hidden })}
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  const list = (
    <div
      data-autocomplete-portal={shouldPortal ? portalMarker : undefined}
      className="pointer-events-auto rounded-xl border border-border/80 bg-popover text-popover-foreground shadow-xl"
    >
      {loading && items.length === 0 ? (
        <div className="px-3 py-3 text-sm text-muted-foreground" role="status">
          {t("search.a11y.searching", { defaultValue: "Searching…" })}
        </div>
      ) : (
        <ul
          id={listId}
          role="listbox"
          className="min-h-0 overflow-y-auto overscroll-contain py-1 [touch-action:pan-y]"
          style={{
            maxHeight: shouldPortal ? position?.maxHeight : MAXIMUM_LIST_HEIGHT,
            minHeight: 0,
          }}
        >
          {items.map((item, index) => (
            <li
              key={getKey(item, index)}
              id={`${listId}-option-${index}`}
              role="option"
              aria-selected={activeIndex === index}
            >
              <button
                type="button"
                className={`flex min-h-12 w-full touch-manipulation items-start gap-2 px-3 py-2 text-left text-base text-foreground transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none md:text-sm ${activeIndex === index ? "bg-muted/60" : ""}`}
                onPointerDown={(event) => {
                  // Preserve input focus, but defer selection until click:
                  // selecting on pointerdown made a touch-scroll gesture
                  // choose the first row before the user could scroll.
                  if (event.pointerType !== "touch") event.preventDefault();
                }}
                onClick={() => onSelect(item)}
              >
                {renderItem(item)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  if (shouldPortal) {
    if (!position) return null;
    // Keep suggestions in the active dialog's interactive subtree. A body
    // portal can be visually above a modal but still miss pointer events when
    // that modal owns the active interaction layer.
    const portalTarget = (portalTargetSelector
      ? anchorRef.current?.closest(portalTargetSelector)
      : null) ?? ((portalInDialog || isMobile)
      ? anchorRef.current?.closest('[role="dialog"]')
      : null) ?? document.body;
    const dialogRect = portalTarget === document.body ? null : portalTarget.getBoundingClientRect();
    return createPortal(
      <div
        data-autocomplete-portal={portalMarker}
        style={{
          position: dialogRect ? "absolute" : "fixed",
          // Above-field lists align their actual bottom edge, not a
          // theoretical full-height top. A single result must stay next
          // to the field rather than float above a large empty gap.
          top: position.top + (position.openAbove ? position.maxHeight : 0) - (dialogRect?.top ?? 0),
          transform: position.openAbove ? "translateY(-100%)" : undefined,
          left: position.left - (dialogRect?.left ?? 0),
          width: position.width,
          maxHeight: position.maxHeight,
          zIndex: 10000,
          pointerEvents: "auto",
        }}
        className="pointer-events-auto overflow-hidden"
      >
        {list}
      </div>,
      portalTarget,
    );
  }

  return (
    <div className="absolute left-0 right-0 top-full z-50 mt-1">
      {list}
    </div>
  );
}